import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { experienciasApi } from '../../api/experiencias'
import { ChipEstado, fechaHora, mensajeTarjetaCompra, momentoDe } from '../../components/InvitacionExperiencia'
import { colors, font } from '../../design/tokens'
import { BotonPrimario, Cargando, ErrorBox, ErrorMsg, Seccion, Titulo } from './comunes'

// Invitados de una experiencia, en el celular: la misma lista y la misma
// tarjeta que en escritorio (lo que se edita en un lado se ve en el otro).
// Los que faltan confirmar van arriba y con sus dos acciones a mano.
export default function MovilExperienciaInvitados() {
  const { id } = useParams()
  const [exp, setExp] = useState(null)
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState(null)
  const [editando, setEditando] = useState(false)
  const [copiado, setCopiado] = useState(null)

  const cargar = useCallback(async () => {
    try {
      const [e, inv] = await Promise.all([experienciasApi.ver(id), experienciasApi.invitados(id)])
      setExp(e.data)
      setDatos(inv.data)
      setError(null)
    } catch {
      setError('No se pudo cargar la experiencia')
    }
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  const copiar = async (inv) => {
    try {
      await navigator.clipboard.writeText(inv.link)
      setCopiado(inv.id)
      setTimeout(() => setCopiado(null), 1800)
    } catch { window.prompt('Copialo a mano:', inv.link) }
  }

  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!exp || !datos) return <Cargando />

  const { resumen, invitados } = datos

  return (
    <div>
      <Titulo>{exp.titulo}</Titulo>
      <p style={{ margin: '-4px 0 12px', fontSize: 12.5, color: colors.textSec, fontFamily: font.sans }}>
        {[exp.cuando, exp.lugar].filter(Boolean).join(' · ')}
      </p>

      {exp.url_publica && (
        <a
          href={`https://wa.me/?text=${encodeURIComponent(mensajeTarjetaCompra(exp.titulo, `${exp.url_publica}/tarjeta`))}`}
          target="_blank" rel="noreferrer"
          style={{ ...estilos.boton, width: '100%', minHeight: 44, marginBottom: 12, boxSizing: 'border-box' }}
        >
          Mandar la tarjeta de compra por WhatsApp
        </a>
      )}

      <div style={estilos.resumen}>
        <div><b style={estilos.num}>{resumen.confirmaron} / {resumen.compras}</b><span style={estilos.lbl}>confirmaron</span></div>
        <div><b style={estilos.num}>{resumen.lugares}</b><span style={estilos.lbl}>lugares</span></div>
      </div>

      <Seccion titulo="Invitados">
        {invitados.length === 0 && (
          <p style={estilos.ayuda}>Todavía no hay compras. Cada nota de venta que entre suma un invitado.</p>
        )}
        {invitados.map((inv) => (
          <div key={inv.id} style={estilos.item}>
            <div style={{ minWidth: 0 }}>
              <b style={{ fontSize: 14.5, color: colors.text, fontFamily: font.sans }}>{inv.nombre || inv.nota_venta.numero}</b>
              <span style={estilos.sub}>
                {inv.cantidad === 1 ? '1 lugar' : `${inv.cantidad} lugares`}
                {inv.turno ? ` · ${inv.turno}` : ''}
                {momentoDe(inv) ? ` · ${fechaHora(momentoDe(inv))}` : ''}
              </span>
            </div>
            <ChipEstado estado={inv.estado} />
            {inv.estado !== 'confirmada' && (
              <div style={estilos.acciones}>
                {inv.whatsapp_url && (
                  <a href={inv.whatsapp_url} target="_blank" rel="noreferrer" style={estilos.boton}>WhatsApp</a>
                )}
                <button type="button" onClick={() => copiar(inv)} style={estilos.boton}>
                  {copiado === inv.id ? '✓ Copiado' : 'Copiar link'}
                </button>
              </div>
            )}
          </div>
        ))}
      </Seccion>

      {editando ? (
        <EditarTarjeta
          experienciaId={id}
          inicial={datos.tarjeta}
          casaVg={datos.casa_vg}
          onListo={(tarjeta) => { setDatos((d) => ({ ...d, tarjeta })); setEditando(false) }}
          onCancelar={() => setEditando(false)}
        />
      ) : (
        <button type="button" onClick={() => setEditando(true)} style={{ ...estilos.boton, width: '100%', minHeight: 44, marginTop: 14 }}>
          Editar la tarjeta
        </button>
      )}
    </div>
  )
}

function EditarTarjeta({ experienciaId, inicial, casaVg, onListo, onCancelar }) {
  const [t, setT] = useState(inicial)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)
  const set = (k, v) => setT((x) => ({ ...x, [k]: v }))

  const guardar = async () => {
    setGuardando(true); setError(null)
    try {
      const { data } = await experienciasApi.guardarTarjeta(experienciaId, t)
      onListo(data.tarjeta)
    } catch (e) {
      const errores = e?.response?.data?.errors
      setError(errores ? Object.values(errores).flat().join(' · ') : 'No se pudo guardar la tarjeta')
    } finally { setGuardando(false) }
  }

  const campo = (k, label, props = {}) => (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={estilos.lbl}>{label}</span>
      <input data-nocaps style={estilos.input} value={t[k] || ''} onChange={(e) => set(k, e.target.value)} {...props} />
    </label>
  )

  return (
    <Seccion titulo="La tarjeta" style={{ marginTop: 14 }}>
      <ErrorBox>{error}</ErrorBox>
      <div style={{ display: 'grid', gap: 12 }}>
        {campo('encabezado', 'Encabezado')}
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={estilos.lbl}>Frase</span>
          <textarea data-nocaps rows={3} style={{ ...estilos.input, resize: 'vertical' }} value={t.frase || ''} onChange={(e) => set('frase', e.target.value)} />
        </label>
        {campo('direccion', casaVg ? 'Dirección (Casa VG, fija)' : 'Dirección', { disabled: casaVg })}
        {campo('parking', casaVg ? 'Parking (Casa VG, fijo)' : 'Parking', { disabled: casaVg })}
        {campo('cuenta_etiqueta', 'Texto de la cuenta regresiva')}
        {campo('nota', 'Frase del final')}
        {campo('whatsapp_aviso', 'WhatsApp que recibe las confirmaciones', { inputMode: 'tel', placeholder: 'Vacío = sin aviso' })}
      </div>
      <BotonPrimario onClick={guardar} disabled={guardando} style={{ marginTop: 14 }}>
        {guardando ? 'Guardando…' : 'Guardar la tarjeta'}
      </BotonPrimario>
      <button type="button" onClick={onCancelar} style={{ ...estilos.boton, width: '100%', minHeight: 44, marginTop: 8 }}>Cancelar</button>
    </Seccion>
  )
}

const estilos = {
  resumen: {
    display: 'flex', gap: 24, padding: '10px 14px', marginBottom: 12,
    background: colors.bgSoft, border: `1px solid ${colors.borderSoft}`, borderRadius: 12,
  },
  num: { display: 'block', fontSize: 18, color: colors.text, fontFamily: font.sans, fontVariantNumeric: 'tabular-nums' },
  lbl: { display: 'block', fontSize: 11, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: font.sans },
  ayuda: { fontSize: 13, color: colors.textSec, fontFamily: font.sans, margin: 0 },
  item: {
    display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 8px', alignItems: 'center',
    padding: '10px 0', borderBottom: `1px solid ${colors.borderSoft}`,
  },
  sub: { display: 'block', fontSize: 12, color: colors.textMeta, fontFamily: font.sans, marginTop: 2 },
  acciones: { gridColumn: '1 / -1', display: 'flex', gap: 8 },
  boton: {
    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 38,
    borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.textSub,
    fontSize: 13.5, fontWeight: 600, fontFamily: font.sans, textDecoration: 'none', cursor: 'pointer',
  },
  input: {
    width: '100%', boxSizing: 'border-box', minHeight: 42, padding: '8px 10px', borderRadius: 10,
    border: `1px solid ${colors.border}`, fontSize: 15, fontFamily: font.sans, color: colors.text, background: colors.bg,
  },
}
